import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Upload, Trash2, Image, Star, Plus, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function imageUrl(path) {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  return `${API}/files/${path}`;
}

async function uploadImage(file) {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${API}/upload`, { method: 'POST', credentials: 'include', body: form });
  if (!res.ok) throw new Error('Upload failed');
  return (await res.json()).path;
}

export default function ContentManager() {
  return (
    <Tabs defaultValue="hero" className="space-y-4">
      <TabsList className="bg-white border border-gray-200 p-1 h-auto flex-wrap">
        <TabsTrigger value="hero" className="px-4 py-2 data-[state=active]:bg-green-50 data-[state=active]:text-green-800" data-testid="content-tab-hero">Hero Background</TabsTrigger>
        <TabsTrigger value="gallery" className="px-4 py-2 data-[state=active]:bg-green-50 data-[state=active]:text-green-800" data-testid="content-tab-gallery">Before/After Gallery</TabsTrigger>
        <TabsTrigger value="recent" className="px-4 py-2 data-[state=active]:bg-green-50 data-[state=active]:text-green-800" data-testid="content-tab-recent">Recent Work</TabsTrigger>
        <TabsTrigger value="testimonials" className="px-4 py-2 data-[state=active]:bg-green-50 data-[state=active]:text-green-800" data-testid="content-tab-testimonials">Testimonials</TabsTrigger>
      </TabsList>

      <TabsContent value="hero"><HeroManager /></TabsContent>
      <TabsContent value="gallery"><GalleryManager /></TabsContent>
      <TabsContent value="recent"><RecentWorkManager /></TabsContent>
      <TabsContent value="testimonials"><TestimonialsManager /></TabsContent>
    </Tabs>
  );
}

// ─── Hero Manager ───────────────────────────────────────────────────────────
function HeroManager() {
  const [hero, setHero] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    const res = await fetch(`${API}/content/hero`);
    setHero(await res.json());
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const path = await uploadImage(file);
      await fetch(`${API}/content/hero`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ background_image_path: path }),
      });
      load();
    } catch (err) { console.error(err); }
    finally { setUploading(false); }
  };

  return (
    <Card className="border border-gray-200" data-testid="hero-manager-card">
      <CardHeader><CardTitle className="text-lg">Hero Background Image</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        {hero?.background_image_path && (
          <div className="relative rounded-lg overflow-hidden border border-gray-200">
            <img src={imageUrl(hero.background_image_path)} alt="Hero background" className="w-full h-48 object-cover" />
            <div className="absolute top-2 right-2 bg-green-700 text-white text-xs px-2 py-1 rounded">Current</div>
          </div>
        )}
        {!hero?.background_image_path && (
          <p className="text-gray-500 text-sm">No custom hero image set. Using default.</p>
        )}
        <div>
          <Label htmlFor="hero-upload" className="cursor-pointer">
            <div className="flex items-center gap-2 border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-green-400 transition-colors">
              {uploading ? <Loader2 className="h-5 w-5 animate-spin text-green-700" /> : <Upload className="h-5 w-5 text-gray-400" />}
              <span className="text-sm text-gray-600">{uploading ? 'Uploading...' : 'Click to upload new hero image'}</span>
            </div>
          </Label>
          <input id="hero-upload" type="file" accept="image/*" className="hidden" onChange={handleUpload} data-testid="hero-upload-input" />
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Gallery Manager (Before/After) ────────────────────────────────────────
function GalleryManager() {
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ title: '', description: '' });
  const [beforeFile, setBeforeFile] = useState(null);
  const [afterFile, setAfterFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const res = await fetch(`${API}/content/gallery`);
    setItems(await res.json());
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!beforeFile || !afterFile || !form.title) return;
    setSaving(true);
    try {
      const [beforePath, afterPath] = await Promise.all([uploadImage(beforeFile), uploadImage(afterFile)]);
      await fetch(`${API}/content/gallery`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ before_image_path: beforePath, after_image_path: afterPath, title: form.title, description: form.description, order: items.length }),
      });
      setAdding(false);
      setForm({ title: '', description: '' });
      setBeforeFile(null);
      setAfterFile(null);
      load();
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    await fetch(`${API}/content/gallery/${id}`, { method: 'DELETE', credentials: 'include' });
    load();
  };

  return (
    <Card className="border border-gray-200" data-testid="gallery-manager-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg">Before/After Gallery</CardTitle>
        <Button size="sm" onClick={() => setAdding(!adding)} className="bg-green-700 hover:bg-green-800 gap-1" data-testid="gallery-add-btn">
          <Plus className="h-4 w-4" /> Add Pair
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {adding && (
          <div className="bg-gray-50 rounded-lg p-4 space-y-3 border border-gray-200" data-testid="gallery-add-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Before Image *</Label>
                <input type="file" accept="image/*" onChange={(e) => setBeforeFile(e.target.files?.[0])} className="mt-1 text-sm" data-testid="gallery-before-input" />
              </div>
              <div>
                <Label className="text-sm">After Image *</Label>
                <input type="file" accept="image/*" onChange={(e) => setAfterFile(e.target.files?.[0])} className="mt-1 text-sm" data-testid="gallery-after-input" />
              </div>
            </div>
            <Input placeholder="Title *" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="gallery-title-input" />
            <Input placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="gallery-desc-input" />
            <div className="flex gap-2">
              <Button size="sm" onClick={handleAdd} disabled={saving || !beforeFile || !afterFile || !form.title} className="bg-green-700 hover:bg-green-800" data-testid="gallery-save-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>Cancel</Button>
            </div>
          </div>
        )}
        {items.length === 0 && !adding && <p className="text-sm text-gray-500">No custom gallery items. Using defaults.</p>}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map((item) => (
            <div key={item.id} className="border border-gray-200 rounded-lg p-3 flex gap-3" data-testid={`gallery-item-${item.id}`}>
              <div className="flex gap-1 shrink-0">
                <img src={imageUrl(item.before_image_path)} alt="Before" className="w-16 h-16 rounded object-cover" />
                <img src={imageUrl(item.after_image_path)} alt="After" className="w-16 h-16 rounded object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{item.title}</p>
                <p className="text-xs text-gray-500 truncate">{item.description}</p>
              </div>
              <Button size="sm" variant="ghost" onClick={() => handleDelete(item.id)} className="text-red-500 hover:text-red-700 shrink-0" data-testid={`gallery-delete-${item.id}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Recent Work Manager ────────────────────────────────────────────────────
function RecentWorkManager() {
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ title: '', description: '' });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const res = await fetch(`${API}/content/recent-work`);
    setItems(await res.json());
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!file || !form.title) return;
    setSaving(true);
    try {
      const path = await uploadImage(file);
      await fetch(`${API}/content/recent-work`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image_path: path, title: form.title, description: form.description, order: items.length }),
      });
      setAdding(false);
      setForm({ title: '', description: '' });
      setFile(null);
      load();
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    await fetch(`${API}/content/recent-work/${id}`, { method: 'DELETE', credentials: 'include' });
    load();
  };

  return (
    <Card className="border border-gray-200" data-testid="recent-work-manager-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg">Recent Work Photos</CardTitle>
        <Button size="sm" onClick={() => setAdding(!adding)} className="bg-green-700 hover:bg-green-800 gap-1" data-testid="recent-add-btn">
          <Plus className="h-4 w-4" /> Add Photo
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {adding && (
          <div className="bg-gray-50 rounded-lg p-4 space-y-3 border border-gray-200" data-testid="recent-add-form">
            <div>
              <Label className="text-sm">Photo *</Label>
              <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0])} className="mt-1 text-sm" data-testid="recent-file-input" />
            </div>
            <Input placeholder="Title *" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="recent-title-input" />
            <Input placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="recent-desc-input" />
            <div className="flex gap-2">
              <Button size="sm" onClick={handleAdd} disabled={saving || !file || !form.title} className="bg-green-700 hover:bg-green-800" data-testid="recent-save-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>Cancel</Button>
            </div>
          </div>
        )}
        {items.length === 0 && !adding && <p className="text-sm text-gray-500">No custom photos. Using defaults.</p>}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {items.map((item) => (
            <div key={item.id} className="border border-gray-200 rounded-lg overflow-hidden" data-testid={`recent-item-${item.id}`}>
              <img src={imageUrl(item.image_path)} alt={item.title} className="w-full h-24 object-cover" />
              <div className="p-2 flex items-center justify-between">
                <p className="text-xs font-medium truncate">{item.title}</p>
                <Button size="sm" variant="ghost" onClick={() => handleDelete(item.id)} className="text-red-500 hover:text-red-700 h-6 w-6 p-0" data-testid={`recent-delete-${item.id}`}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Testimonials Manager ───────────────────────────────────────────────────
function TestimonialsManager() {
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', location: '', rating: 5, text: '', service: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const res = await fetch(`${API}/content/testimonials`);
    setItems(await res.json());
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!form.name || !form.text) return;
    setSaving(true);
    try {
      await fetch(`${API}/content/testimonials`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, order: items.length }),
      });
      setAdding(false);
      setForm({ name: '', location: '', rating: 5, text: '', service: '' });
      load();
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    await fetch(`${API}/content/testimonials/${id}`, { method: 'DELETE', credentials: 'include' });
    load();
  };

  return (
    <Card className="border border-gray-200" data-testid="testimonials-manager-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg">Testimonials</CardTitle>
        <Button size="sm" onClick={() => setAdding(!adding)} className="bg-green-700 hover:bg-green-800 gap-1" data-testid="testimonials-add-btn">
          <Plus className="h-4 w-4" /> Add Testimonial
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {adding && (
          <div className="bg-gray-50 rounded-lg p-4 space-y-3 border border-gray-200" data-testid="testimonials-add-form">
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="testimonial-name-input" />
              <Input placeholder="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="testimonial-location-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Service" value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} data-testid="testimonial-service-input" />
              <div className="flex items-center gap-2">
                <Label className="text-sm shrink-0">Rating:</Label>
                <div className="flex gap-0.5">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} type="button" onClick={() => setForm({ ...form, rating: n })} data-testid={`testimonial-star-${n}`}>
                      <Star className={`h-5 w-5 ${n <= form.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <Textarea placeholder="Testimonial text *" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} rows={3} data-testid="testimonial-text-input" />
            <div className="flex gap-2">
              <Button size="sm" onClick={handleAdd} disabled={saving || !form.name || !form.text} className="bg-green-700 hover:bg-green-800" data-testid="testimonial-save-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>Cancel</Button>
            </div>
          </div>
        )}
        {items.length === 0 && !adding && <p className="text-sm text-gray-500">No custom testimonials. Using defaults.</p>}
        <div className="space-y-2">
          {items.map((item) => (
            <div key={item.id} className="border border-gray-200 rounded-lg p-3 flex items-start gap-3" data-testid={`testimonial-item-${item.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <p className="font-medium text-sm">{item.name}</p>
                  <span className="text-xs text-gray-400">{item.location}</span>
                  <div className="flex gap-0.5 ml-auto">
                    {[...Array(item.rating)].map((_, i) => <Star key={i} className="h-3 w-3 fill-yellow-400 text-yellow-400" />)}
                  </div>
                </div>
                <p className="text-xs text-gray-600 line-clamp-2">{item.text}</p>
                {item.service && <p className="text-xs text-green-700 mt-1">{item.service}</p>}
              </div>
              <Button size="sm" variant="ghost" onClick={() => handleDelete(item.id)} className="text-red-500 hover:text-red-700 shrink-0" data-testid={`testimonial-delete-${item.id}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
